import { z } from 'zod';

export const blockedStateSchema = z.enum(['WITHDRAWN', 'EXPIRED', 'ACCEPTED', 'NO_LONGER_VALID']);
export type BlockedState = z.infer<typeof blockedStateSchema>;

export const invitationRoleSchema = z.enum(['TENANT_ADMIN', 'CAMPAIGN_ANALYST']);
export type InvitationRole = z.infer<typeof invitationRoleSchema>;

export const availablePreviewSchema = z.object({
  kind: z.literal('available'),
  tenant_name: z.string(),
  campaign_name: z.string().nullable(),
  role: invitationRoleSchema,
  invited_by_email: z.string().nullable(),
});
export type AvailablePreview = z.infer<typeof availablePreviewSchema>;

export const unavailablePreviewSchema = z.object({
  kind: z.literal('unavailable'),
  state: blockedStateSchema,
});
export type UnavailablePreview = z.infer<typeof unavailablePreviewSchema>;

export const previewSchema = z.discriminatedUnion('kind', [
  availablePreviewSchema,
  unavailablePreviewSchema,
]);
export type InvitationPreview = z.infer<typeof previewSchema>;

export const declineSchema = z.union([z.null(), unavailablePreviewSchema]);
export type DeclineResponse = z.infer<typeof declineSchema>;

export const acceptSuccessSchema = z.object({
  status: z.literal('accepted'),
  tenant_id: z.string(),
});
export type AcceptSuccess = z.infer<typeof acceptSuccessSchema>;

export const acceptRefusalStatusSchema = z.enum([
  'withdrawn',
  'expired',
  'already_accepted',
  'no_longer_valid',
]);
export type AcceptRefusalStatus = z.infer<typeof acceptRefusalStatusSchema>;

export const acceptRefusalSchema = z.object({
  status: acceptRefusalStatusSchema,
});
export type AcceptRefusal = z.infer<typeof acceptRefusalSchema>;

// Declared here rather than in `../api/schemas.ts`: that file is the edge for tenant-scoped routes; these routes carry no tenant id.
// Named after the API's own `InvitationAcceptanceOut` union: both accept
// routes (by token, by id) return this shape.
export const acceptanceSchema = z.discriminatedUnion('status', [
  acceptSuccessSchema,
  acceptRefusalSchema,
]);
export type AcceptResponse = z.infer<typeof acceptanceSchema>;

/** One row of `GET /invitations`: every invitation addressed to the
 *  caller's own verified email. Never `EXPIRED`/`ACCEPTED`/`NO_LONGER_VALID`
 *  -- the endpoint only ever lists a still-open or a withdrawn invitation
 *  (api's `list_invitations_for_email`); a state outside that pair is a
 *  contract drift, not a row this screen must render. */
export const myInvitationStateSchema = z.enum(['PENDING', 'WITHDRAWN']);
export type MyInvitationState = z.infer<typeof myInvitationStateSchema>;

export const myInvitationSchema = z.object({
  id: z.string(),
  tenant_name: z.string(),
  role: invitationRoleSchema,
  expires_at: z.string(),
  state: myInvitationStateSchema,
});
export type MyInvitation = z.infer<typeof myInvitationSchema>;

export const myInvitationsSchema = z.object({
  items: z.array(myInvitationSchema),
});

const BLOCKED_STATE_MAP: Record<AcceptRefusalStatus | BlockedState, BlockedState> = {
  withdrawn: 'WITHDRAWN',
  expired: 'EXPIRED',
  already_accepted: 'ACCEPTED',
  no_longer_valid: 'NO_LONGER_VALID',
  WITHDRAWN: 'WITHDRAWN',
  EXPIRED: 'EXPIRED',
  ACCEPTED: 'ACCEPTED',
  NO_LONGER_VALID: 'NO_LONGER_VALID',
};

export function toBlockedState(status: AcceptRefusalStatus | BlockedState): BlockedState {
  return BLOCKED_STATE_MAP[status];
}
